import { useTranslation } from "react-i18next";
import { useTheme } from "antd-style";

export const requiredRule = (t) => [
  {
    required: true,
    message: t("config.instance.error.required", "This is a required field."),
  },
];

export const isValidCoreValue = (value) => {
  const regexInteger = /^(?!0)\d+$/;
  const regexDecimal = /^(0\.[0-9]+)$/;
  const regexWithUnit = /^(?!0)\d+m$/;

  return (
    regexInteger.test(value) ||
    (regexDecimal.test(value) && parseFloat(value) > 0) ||
    regexWithUnit.test(value)
  );
};

export const isValidMemoryValue = (value) => {
  const regex = /^(?!0)\d+(Gi|Mi)$/;
  return regex.test(value);
};

export const isValidDiskSize = (input) => {
  const regex = /^(?!0)\d+(Gi|Mi)$/;
  return regex.test(input);
};

export const isGreaterThanZero = (char) => {
  const num = parseFloat(char);
  return !isNaN(num) && num >= 0;
};

export const isValidWorkMemoryValue = (value) => {
  const regex = /^(?!0)\d+G$/;
  return regex.test(value);
};

export const isValidPoolValue = (value) => {
  const regex = /^(?!0)\d+G$/;
  return regex.test(value);
};

export const checkDiskSizeIncrease = (defaultValue, inputValue) => {
  let defaultValueInt = parseInt(defaultValue, 10);
  const defaultValueUnit = defaultValue.replace(/\d+/g, "");
  if (defaultValueUnit === "Gi") {
    defaultValueInt *= 1024;
  }

  let inputValueInt = parseInt(inputValue, 10);
  const inputValueUnit = inputValue.replace(/\d+/g, "");
  if (inputValueUnit === "Gi") {
    inputValueInt *= 1024;
  }
  if (inputValueInt >= defaultValueInt) return true;
  return false;
};

const diskDescription =
  "The format is a positive integer followed by Gi or Mi, for example: 100Gi, 2000Mi";
const memoryDescription =
  "The format is a positive integer followed by Gi or Mi, for example: 5Gi, 2000Mi.";
const coreDescription =
  "The format is a positive integer, a decimal less than 1, or a positive integer followed by the letter m, for example: 1, 0.5, or 2000m.";

const replicasDescription = "The value is greater than or equal to zero";
const workerMemDescription =
  "The format is a positive integer followed by G, for example: 58G.";

const poolDescription =
  "The format is a positive integer followed by G, for example: 10G.";

export const useBasicComponents = (action = "create", defaultValues = {}) => {
  const { t } = useTranslation();
  const token = useTheme();

  const replicas = ({ group, dataIndex, ...restProps }) => ({
    i18nTitle: "config.instance.form.replicas",
    title: t("config.instance.form.replicas", "Replicas"),
    dataIndex: dataIndex || `${group}.replicas`,
    valueType: "digit",
    width: "sm",
    formItemProps: {
      rules: [
        ...requiredRule(t),
        {
          validator(_, value) {
            if (isGreaterThanZero(value)) {
              return Promise.resolve();
            }
            return Promise.reject(
              new Error(
                t("config.instance.error.replicas", replicasDescription),
              ),
            );
          },
        },
      ],
    },
    ...restProps,
  });

  const replicasReadOnly = ({ group, dataIndex, ...restProps }) => ({
    i18nTitle: "config.instance.form.replicas",
    title: t("config.instance.form.replicas", "Replicas"),
    dataIndex: dataIndex || `${group}.replicas`,
    valueType: "digit",
    width: "sm",
    fieldProps: {
      // readOnly: true,
      // style: {
      //   pointerEvents: "none",
      //   backgroundColor: token.controlItemBgActiveDisabled,
      // },
      disabled: true,
    },
    ...restProps,
  });

  const core = ({ group, dataIndex, ...restProps }) => ({
    i18nTitle: "config.instance.form.core",
    title: t("config.instance.form.core", "Core"),
    dataIndex: dataIndex || `${group}.core`,
    width: "sm",
    formItemProps: {
      rules: [
        ...requiredRule(t),
        {
          validator(_, value) {
            if (isValidCoreValue(value)) {
              return Promise.resolve();
            }
            return Promise.reject(
              new Error(t("config.instance.error.core", coreDescription)),
            );
          },
        },
      ],
    },
    ...restProps,
  });

  const memory = ({ group, dataIndex, ...restProps }) => ({
    i18nTitle: "config.instance.form.memory",
    title: t("config.instance.form.memory", "Memory"),
    dataIndex: dataIndex || `${group}.memory`,
    width: "sm",
    formItemProps: {
      rules: [
        ...requiredRule(t),
        {
          validator(_, value) {
            if (isValidMemoryValue(value)) {
              return Promise.resolve();
            }
            return Promise.reject(
              new Error(t("config.instance.error.memory", memoryDescription)),
            );
          },
        },
      ],
    },
    ...restProps,
  });

  const version = ({ group, dataIndex, ...restProps }) => ({
    i18nTitle: "config.instance.form.version",
    title: t("config.instance.form.version", "Version"),
    dataIndex: dataIndex || `${group}.version`,
    width: "sm",
    fieldProps: {
      // readOnly: true,
      // style: {
      //   pointerEvents: "none",
      //   backgroundColor: token.controlItemBgActiveDisabled,
      // },
      disabled: true,
    },
    ...restProps,
  });

  const dataDisk = ({ group, dataIndex, ...restProps }) => ({
    i18nTitle: "config.instance.form.dataDisk",
    title: t("config.instance.form.dataDisk", "Data Disk"),
    dataIndex: dataIndex || `${group}.dataDisk`,
    width: "sm",
    formItemProps: {
      rules: [
        ...requiredRule(t),
        {
          validator(_, value) {
            if (!isValidDiskSize(value)) {
              return Promise.reject(
                new Error(t("config.instance.error.disk", diskDescription)),
              );
            }
            if (action === "update") {
              const defaultValue = defaultValues[`${group}.dataDisk`];
              if (!checkDiskSizeIncrease(defaultValue, value)) {
                return Promise.reject(
                  new Error(
                    new Error(
                      t("config.instance.error.diskSize", { defaultValue }),
                    ),
                  ),
                );
              }
            }
            return Promise.resolve();
          },
        },
      ],
    },
    ...restProps,
  });

  const logDisk = ({ group, dataIndex, ...restProps }) => ({
    title: t("config.instance.form.logDisk", "Log Disk"),
    dataIndex: dataIndex || `${group}.logDisk`,
    width: "sm",
    formItemProps: {
      rules: [
        ...requiredRule(t),
        {
          validator(_, value) {
            if (!isValidDiskSize(value)) {
              return Promise.reject(
                new Error(t("config.instance.error.disk", diskDescription)),
              );
            }
            if (action === "update") {
              const defaultValue = defaultValues[`${group}.logDisk`];
              if (!checkDiskSizeIncrease(defaultValue, value)) {
                return Promise.reject(
                  new Error(
                    new Error(
                      t("config.instance.error.diskSize", { defaultValue }),
                    ),
                  ),
                );
              }
            }

            return Promise.resolve();
          },
        },
      ],
    },
    ...restProps,
  });

  function workerMem({ group, dataIndex, ...restProps }) {
    return {
      title: t("config.instance.form.workerMem", "Worker Mem"),
      dataIndex: dataIndex || `${group}.workerMem`,
      width: "sm",
      formItemProps: {
        rules: [
          ...requiredRule(t),
          {
            validator(_, value) {
              if (isValidWorkMemoryValue(value)) {
                return Promise.resolve();
              }
              return Promise.reject(
                new Error(
                  t("config.instance.error.workerMem", workerMemDescription),
                ),
              );
            },
          },
        ],
      },
      ...restProps,
    };
  }

  const poolSize = ({ group, dataIndex, ...restProps }) => ({
    i18nTitle: "config.instance.form.poolSize",
    title: t("config.instance.form.poolSize", "Pool Size"),
    dataIndex: dataIndex || `${group}.poolSize`,
    width: "sm",
    formItemProps: {
      rules: [
        ...requiredRule(t),
        {
          validator(_, value) {
            if (isValidPoolValue(value)) {
              return Promise.resolve();
            }
            return Promise.reject(
              new Error(
                t("config.instance.error.poolValidation", poolDescription),
              ),
            );
          },
        },
      ],
    },
    ...restProps,
  });

  const javaOpts = (group) => ({
    title: t("config.instance.form.javaOpts", "Java Opts"),
    dataIndex: `${group}.javaOpts`,
    width: "l",
  });

  const nodegroup = ({ group, dataIndex, options, ...restProps }) => ({
    i18nTitle: "config.instance.form.nodegroup",
    title: t("config.instance.form.nodegroup", "Nodegroup"),
    dataIndex: dataIndex || `${group}.nodegroup`,
    valueType: "select",
    width: "sm",
    fieldProps: {
      options: options || [
        { label: "master", value: "master" },
        { label: "database", value: "database" },
      ],
    },
    formItemProps: {
      rules: [...requiredRule(t)],
    },
    ...restProps,
  });

  return {
    replicas,
    replicasReadOnly,
    core,
    memory,
    version,
    dataDisk,
    logDisk,
    workerMem,
    poolSize,
    javaOpts,
    nodegroup,
  };
};
